import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // L'écran d'introduction s'affiche à chaque véritable ouverture / actualisation
  // (flag en mémoire — jamais pendant la navigation interne)
  const [skipSplash] = useState(() => !!window.__spiritulSplashSeen);
  const [target] = useState(() => sessionStorage.getItem('splash_target') || '/menu');
  const startRef = useRef(Date.now());
  const leftRef = useRef(false);

  const goToApp = React.useCallback(() => {
    if (leftRef.current) return;
    leftRef.current = true;
    window.__spiritulSplashSeen = true;
    sessionStorage.removeItem('splash_target');
    setLeaving(true);
    setTimeout(() => navigate(target), 450);
  }, [navigate, target]);

  useEffect(() => {
    if (skipSplash) {
      sessionStorage.removeItem('splash_target');
      navigate(target, { replace: true });
      return undefined;
    }
    setTimeout(() => setIsVisible(true), 100);
    // Garde-fou absolu : jamais plus de 9 s, même si une ressource traîne
    const capTimer = setTimeout(goToApp, 9000);
    return () => clearTimeout(capTimer);
  }, [navigate, target, skipSplash, goToApp]);

  // Dès que l'essentiel (profil / accès) est prêt, transition douce
  // après un affichage minimal de ~1,5 s pour que l'animation soit visible
  useEffect(() => {
    if (skipSplash || authLoading) return undefined;
    const minMs = 1500;
    const elapsed = Date.now() - startRef.current;
    const readyTimer = setTimeout(goToApp, Math.max(0, minMs - elapsed));
    return () => clearTimeout(readyTimer);
  }, [authLoading, skipSplash, goToApp]);

  if (skipSplash) return null;

  return (
    <div
      className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6 cursor-pointer overflow-hidden relative"
      onClick={goToApp}
    >
      <div className={`text-center transition-opacity duration-700 ${isVisible && !leaving ? 'opacity-100' : 'opacity-0'}`} style={{ marginTop: '-16vh' }}>

        {/* Le pissenlit à la place de l'apostrophe — il respire, une graine s'envole, puis Spiritu’L apparaît */}
        <div className="relative mx-auto" style={{ width: '240px', overflow: 'visible' }}>
          <h1 className="splash-title splash-wordmark whitespace-nowrap" data-testid="splash-title" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 200 }}>
            Spiritu<span style={{ display: 'inline-block', width: '16px' }}></span>L
          </h1>

          {/* Le pissenlit respire — il remplace l'apostrophe */}
          <img
            src="/pissenlit-splash-simple.png?v=2"
            alt=""
            className="absolute pointer-events-none splash-breathe"
            style={{ left: '152px', bottom: '78px', width: '74px', maxWidth: 'none' }}
            data-testid="splash-pissenlit"
          />

          {/* 3 aigrettes — elles se détachent de la tête du pissenlit et s'envolent, l'une après l'autre */}
          <div className="absolute pointer-events-none splash-aigrette splash-aigrette-1" style={{ left: '79%', bottom: '160px' }}>
            <svg width="18" height="18" viewBox="0 0 20 20">
              <path d="M10 17 Q8.8 13 10 8.5" stroke="#F79AAE" strokeWidth="0.7" fill="none" strokeLinecap="round" />
              <line x1="10" y1="8.5" x2="4.5" y2="4" stroke="#F79AAE" strokeWidth="0.5" opacity="0.9" />
              <line x1="10" y1="8.5" x2="14.5" y2="3" stroke="#F79AAE" strokeWidth="0.5" opacity="0.9" />
              <line x1="10" y1="8.5" x2="10.5" y2="2" stroke="#F79AAE" strokeWidth="0.5" opacity="0.9" />
              <line x1="10" y1="8.5" x2="6" y2="1.8" stroke="#F79AAE" strokeWidth="0.42" opacity="0.7" />
              <line x1="10" y1="8.5" x2="15.8" y2="6" stroke="#F79AAE" strokeWidth="0.42" opacity="0.7" />
            </svg>
          </div>
          <div className="absolute pointer-events-none splash-aigrette splash-aigrette-2" style={{ left: '66%', bottom: '168px' }}>
            <svg width="15" height="15" viewBox="0 0 20 20">
              <line x1="10" y1="16" x2="10" y2="8" stroke="#F79AAE" strokeWidth="0.65" strokeLinecap="round" />
              <line x1="10" y1="8" x2="5.5" y2="3.5" stroke="#F79AAE" strokeWidth="0.48" opacity="0.85" />
              <line x1="10" y1="8" x2="15" y2="4.5" stroke="#F79AAE" strokeWidth="0.48" opacity="0.85" />
              <line x1="10" y1="8" x2="8.5" y2="2" stroke="#F79AAE" strokeWidth="0.42" opacity="0.7" />
              <line x1="10" y1="8" x2="13" y2="2.2" stroke="#F79AAE" strokeWidth="0.42" opacity="0.7" />
            </svg>
          </div>
          <div className="absolute pointer-events-none splash-aigrette splash-aigrette-3" style={{ left: '87%', bottom: '150px' }}>
            <svg width="13" height="13" viewBox="0 0 20 20">
              <path d="M10 16.5 Q11 13 10 9" stroke="#F79AAE" strokeWidth="0.6" fill="none" strokeLinecap="round" />
              <line x1="10" y1="9" x2="5" y2="5" stroke="#F79AAE" strokeWidth="0.45" opacity="0.85" />
              <line x1="10" y1="9" x2="14" y2="3.8" stroke="#F79AAE" strokeWidth="0.45" opacity="0.85" />
              <line x1="10" y1="9" x2="10" y2="2.8" stroke="#F79AAE" strokeWidth="0.45" opacity="0.85" />
            </svg>
          </div>

          {/* Le slogan — apparaît avec le titre */}
          <p className="splash-title splash-slogan font-lora text-center whitespace-nowrap" data-testid="splash-slogan" style={{ marginLeft: '-60px', marginRight: '-60px' }}>
            Repose ton <span style={{ color: '#F79AAE' }}>esprit</span>. Éveille ta <span style={{ color: '#F79AAE' }}>spiritualité</span>
          </p>
        </div>
      </div>

      <style>{`
        /* Le pissenlit respire — doucement, comme un souffle */
        .splash-breathe {
          transform-origin: 50% 45%;
          animation: splash-breathe 4s ease-in-out infinite;
          will-change: transform;
        }
        @keyframes splash-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.05); }
        }

        /* Puis Spiritu’L apparaît — assez tôt pour être visible dès l'affichage minimal */
        .splash-title {
          opacity: 0;
          animation: splash-title 1s ease-out 0.3s forwards;
        }
        @keyframes splash-title {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Les 3 aigrettes — elles naissent de la tête du pissenlit, s'envolent
           en ondulant comme la graine originelle, puis une autre prend le relais. */
        .splash-aigrette {
          opacity: 0;
          will-change: transform, opacity;
        }
        .splash-aigrette-1 { animation: aigrette-fly-a 9s ease-in-out 0.8s infinite; }
        .splash-aigrette-2 { animation: aigrette-fly-b 11s ease-in-out 2s infinite; }
        .splash-aigrette-3 { animation: aigrette-fly-c 8s ease-in-out 3.2s infinite; }
        /* Envol vers le haut-droit, ondulation ample (chemin de la graine originelle) */
        @keyframes aigrette-fly-a {
          0%   { opacity: 0; transform: translate(0, 0) rotate(0deg); }
          8%   { opacity: 0.85; }
          28%  { transform: translate(16px, -26px) rotate(9deg); }
          48%  { transform: translate(26px, -54px) rotate(-6deg); opacity: 0.75; }
          68%  { transform: translate(44px, -84px) rotate(10deg); opacity: 0.45; }
          82%  { opacity: 0; transform: translate(56px, -108px) rotate(14deg); }
          100% { opacity: 0; transform: translate(0, 0) rotate(0deg); }
        }
        /* Envol vers le haut-gauche, plus lent, balancement pendulaire */
        @keyframes aigrette-fly-b {
          0%   { opacity: 0; transform: translate(0, 0) rotate(0deg); }
          7%   { opacity: 0.7; }
          25%  { transform: translate(-12px, -22px) rotate(-8deg); }
          45%  { transform: translate(-6px, -48px) rotate(6deg); opacity: 0.65; }
          65%  { transform: translate(-20px, -76px) rotate(-10deg); opacity: 0.4; }
          80%  { opacity: 0; transform: translate(-26px, -98px) rotate(-13deg); }
          100% { opacity: 0; transform: translate(0, 0) rotate(0deg); }
        }
        /* Envol presque vertical, petite spirale légère */
        @keyframes aigrette-fly-c {
          0%   { opacity: 0; transform: translate(0, 0) rotate(0deg); }
          9%   { opacity: 0.75; }
          30%  { transform: translate(7px, -24px) rotate(6deg); }
          52%  { transform: translate(-2px, -52px) rotate(-5deg); opacity: 0.65; }
          72%  { transform: translate(8px, -80px) rotate(8deg); opacity: 0.35; }
          85%  { opacity: 0; transform: translate(4px, -100px) rotate(4deg); }
          100% { opacity: 0; transform: translate(0, 0) rotate(0deg); }
        }

        /* Titre — Manrope ExtraLight, même famille que les cartes, finesse Lato */
        .splash-wordmark {
          font-size: 50px;
          font-weight: 200;
          line-height: 1.2;
          color: #2b2b2b;
          letter-spacing: 0.03em;
        }
        /* Slogan — Lora Regular, gris chaud, rythme élégant */
        .splash-slogan {
          color: #8B8178;
          font-size: 0.9rem;
          font-weight: 400;
          letter-spacing: 0.05em;
          line-height: 1.65;
          margin-top: 17px;
        }
      `}</style>
    </div>
  );
};

export default Home;
